import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronRight, Check, Tag, MapPin, Search, X } from "lucide-react";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { useRetailerConfig } from "../hooks/useRetailerConfig";
import {
  useCategories,
  useDollarGeneralCategories,
  useHomeDepotCategories,
  useTargetCategories,
  useUpdateUserConfig,
} from "../hooks/useBotFilters";
import { useStores } from "../hooks/useStores";
import { geocodeZipCode } from "../utils/geocoding";
import { CategoriesFilter } from "./filtertypes";
import ZipCodeDisplay from "./ZipCodeDisplay";
import LoadingState from "./LoadingState";
import ErrorState from "./ErrorState";
import AuthButton from "./AuthButton";
import { getRetailerStoreLimit } from "../utils/retailerStoreLimits";
import { getUserStoresForRetailer } from "../config/retailers";

import {
  calculateDistance,
  normalizeStore,
  type StoreType,
  type StoreWithDistance,
} from "./BotFiltersOnboardingModal.utils";

interface BotFiltersOnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onClose: () => void;
}

type StepType = "categories" | "stores";

export default function BotFiltersOnboardingModal({
  isOpen,
  onComplete,
  onClose,
}: BotFiltersOnboardingModalProps) {
  const { user, load, updateUser } = useUserContext();
  const { t } = useTranslation();
  const retailerConfig = useRetailerConfig();
  const updateUserConfigMutation = useUpdateUserConfig();
  
  // Stores data
  const { stores } = useStores(retailerConfig.retailer);

  // Use retailer-specific category fetching
  const {
    data: walmartCategories,
    isLoading: isWalmartCategoriesLoading,
    error: walmartCategoriesError,
  } = useCategories();

  const {
    data: dollarGeneralCategories,
    isLoading: isDollarGeneralCategoriesLoading,
    error: dollarGeneralCategoriesError,
  } = useDollarGeneralCategories();

  const {
    data: homeDepotCategories,
    isLoading: isHomeDepotCategoriesLoading,
    error: homeDepotCategoriesError,
  } = useHomeDepotCategories();

  const {
    data: targetCategories,
    isLoading: isTargetCategoriesLoading,
    error: targetCategoriesError,
  } = useTargetCategories();

  // Determine which categories to use based on retailer
  const categories =
    retailerConfig.retailer === "home_depot"
      ? homeDepotCategories
      : retailerConfig.retailer === "target"
      ? targetCategories
      : retailerConfig.retailer === "dollar_general"
      ? dollarGeneralCategories
      : walmartCategories;

  const isCategoriesLoading =
    retailerConfig.retailer === "home_depot"
      ? isHomeDepotCategoriesLoading
      : retailerConfig.retailer === "target"
      ? isTargetCategoriesLoading
      : retailerConfig.retailer === "dollar_general"
      ? isDollarGeneralCategoriesLoading
      : isWalmartCategoriesLoading;

  const categoriesError =
    retailerConfig.retailer === "home_depot"
      ? homeDepotCategoriesError
      : retailerConfig.retailer === "target"
      ? targetCategoriesError
      : retailerConfig.retailer === "dollar_general"
      ? dollarGeneralCategoriesError
      : walmartCategoriesError;

  // Determine which steps to show based on what's missing
  // We no longer check for categories - users don't need to have categories set up
  const needsCategories = false;
  const needsStores =
    retailerConfig.isFeatureEnabled("stores") &&
    (!user || getUserStoresForRetailer(user, retailerConfig.retailer).length === 0);

  // Only show steps that are needed
  const steps: StepType[] = [];
  if (needsCategories) steps.push("categories");
  if (needsStores) steps.push("stores");

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>("");
  
  // Store selection state
  const [zipCode, setZipCode] = useState("");
  const [nearbyStores, setNearbyStores] = useState<StoreWithDistance[]>([]);
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);
  const [storeSearchQuery, setStoreSearchQuery] = useState("");

  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;

  // Initialize state from user data
  useEffect(() => {
    if (user && isOpen) {
      setSelectedCategories(retailerConfig.getUserCategories(user));
      setSelectedStores(getUserStoresForRetailer(user, retailerConfig.retailer));
    }
  }, [user, isOpen, retailerConfig]);

  // Initialize zip code
  useEffect(() => {
    if (user?.zipCode) setZipCode(user.zipCode);
  }, [user?.zipCode]);

  // Geocode and find nearby stores
  useEffect(() => {
    if (!zipCode || !/^\d{5}$/.test(zipCode) || !stores) return;
    setIsGeocodingLoading(true);
    setNearbyStores([]);
    const currentRetailer = retailerConfig.retailer;
    
    geocodeZipCode(zipCode).then(async (coords) => {
      if (!coords) { setIsGeocodingLoading(false); return; }
      const storeData = stores as StoreType[];
      const normalized = storeData.map(s => normalizeStore(s, currentRetailer));
      const withGeo = normalized.filter(s => s.normalizedGeo?.lat && s.normalizedGeo?.long && !isNaN(s.normalizedGeo.lat) && !isNaN(s.normalizedGeo.long));
      const withDistance = withGeo.map(s => ({ ...s, distance: calculateDistance(coords.lat, coords.long, s.normalizedGeo!.lat, s.normalizedGeo!.long) }))
        .filter(s => s.distance! <= 100).sort((a, b) => (a.distance || 0) - (b.distance || 0)).slice(0, 150);
      setNearbyStores(withDistance);
    }).catch(() => {
      const storeData = stores as StoreType[];
      setNearbyStores(storeData.map(s => normalizeStore(s, currentRetailer)).slice(0, 150));
    }).finally(() => setIsGeocodingLoading(false));
  }, [zipCode, stores, retailerConfig.retailer]);

  // Reset step when modal opens or steps change
  useEffect(() => {
    if (isOpen && steps.length > 0) {
      setCurrentStepIndex(0);
    }
  }, [isOpen, steps.length]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSaving) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isSaving, onClose]);

  const getStoreLimit = getRetailerStoreLimit;

  const saveConfiguration = async (
    newSelectedCategories?: string[],
    newSelectedStores?: string[]
  ) => {
    if (!user) return;

    setIsSaving(true);
    try {
      const updateData = retailerConfig.prepareUserUpdate({
        categories: newSelectedCategories ?? selectedCategories,
        stores: newSelectedStores ?? selectedStores,
      });

      await updateUserConfigMutation.mutateAsync(updateData);
      await load(); // Reload user data

      if (newSelectedCategories !== undefined) {
        setSelectedCategories(newSelectedCategories);
      }
      if (newSelectedStores !== undefined) {
        setSelectedStores(newSelectedStores);
      }
    } catch (error: any) {
      const errorMessage = t("botFilters.failedToSaveBotFilters") as string;
      setError(errorMessage);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCategory = async (rootKey: string) => {
    if (!user || isSaving) return;

    const isSelected = selectedCategories.includes(rootKey);
    const newSelectedCategories = isSelected
      ? selectedCategories.filter((key) => key !== rootKey)
      : [...selectedCategories, rootKey];

    setSelectedCategories(newSelectedCategories);
    await saveConfiguration(newSelectedCategories);
  };

  const toggleAllCategories = async () => {
    if (!user || !categories || isSaving) return;

    const allSelected = categories.every((category) =>
      selectedCategories.includes(category.rootKey)
    );

    const newSelectedCategories = allSelected
      ? []
      : categories.map((cat) => cat.rootKey);

    setSelectedCategories(newSelectedCategories);
    await saveConfiguration(newSelectedCategories);
  };

  const handleNext = async () => {
    setError("");
    setIsSaving(true);

    try {
      // Validate current step
      if (currentStep === "categories" && selectedCategories.length === 0) {
        setError(t("botFilters.pleaseSelectAtLeastOneCategory") || "Please select at least one category");
        return;
      }
      if (currentStep === "stores") {
        let validStores = selectedStores.filter(s => s !== "");
        if (validStores.length === 0) {
          setError(t("botFilters.pleaseSelectAtLeastOneStore") || "Please select at least one store");
          return;
        }
        // For costco, resolve to warehouse_number-wh format
        if (retailerConfig.retailer === "costco" && stores) {
          validStores = validStores.map((id) => {
            if (/^\d+-wh$/.test(id)) return id;
            const storeData = stores as any[];
            const match = storeData.find((s: any) =>
              s.warehouse_number ? `${s.warehouse_number}-wh` === id : s.address === id
            );
            if (match?.warehouse_number) return `${match.warehouse_number}-wh`;
            return id;
          });
        }
        const updateData: Record<string, unknown> = {};
        const timestamp = Date.now();
        if (retailerConfig.retailer === "walmart") { updateData.storeIds = validStores; updateData.lastStoreChange = timestamp; }
        else if (retailerConfig.retailer === "home_depot") { updateData.homeDepotStoreIds = validStores; updateData.lastHomeDepotStoreChange = timestamp; }
        else if (retailerConfig.retailer === "target") { updateData.targetStoreIds = validStores; updateData.lastTargetStoreChange = timestamp; }
        else if (retailerConfig.retailer === "dollar_general") { updateData.dollarGeneralStoreIds = validStores; updateData.lastDollarGeneralStoreChange = timestamp; }
        else if (retailerConfig.retailer === "bestbuy") { updateData.bestbuyStoreIds = validStores; updateData.lastBestBuyStoreChange = timestamp; }
        else if (retailerConfig.retailer === "lowes") { updateData.lowesStoreIds = validStores; updateData.lastLowesStoreChange = timestamp; }
        else if (retailerConfig.retailer === "costco") { updateData.costcoStoreIds = validStores; updateData.lastCostcoStoreChange = timestamp; }
        else if (retailerConfig.retailer === "tractor_supply") { updateData.tractorSupplyStoreIds = validStores; updateData.lastTractorSupplyStoreChange = timestamp; }
        else if (retailerConfig.retailer === "northern_tool") { updateData.northernToolStoreIds = validStores; updateData.lastNorthernToolStoreChange = timestamp; }
        else if (retailerConfig.retailer === "samsclub") { updateData.samsClubStoreIds = validStores; updateData.lastSamsClubStoreChange = timestamp; }
        await updateUser(updateData);
        await load();
      }

      if (isLastStep) {
        onComplete();
      } else {
        setCurrentStepIndex(currentStepIndex + 1);
      }
    } catch (err: any) {
      setError(err?.message || t("botFilters.failedToSaveBotFilters"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStepIndex(currentStepIndex - 1);
      setError("");
    }
  };

  const canProceed = () => {
    if (currentStep === "categories") return selectedCategories.length > 0;
    if (currentStep === "stores") return selectedStores.filter(s => s !== "").length > 0;
    return false;
  };

  // Don't render if modal is closed or if there are no steps needed
  if (!isOpen || steps.length === 0) return null;

  const filteredStores = currentStep === "stores"
    ? nearbyStores.filter(s => !storeSearchQuery || s.normalizedId.toLowerCase().includes(storeSearchQuery.toLowerCase()) || s.normalizedAddress.toLowerCase().includes(storeSearchQuery.toLowerCase()))
    : [];

  const getStoreLabel = (store: StoreWithDistance) => {
    const rawStore = store as unknown as Partial<StoreType> & {
      Name?: string;
      store_title?: string;
      name?: string;
      warehouse_name?: string;
    };

    if (retailerConfig.retailer === "target" && rawStore.Name) {
      return rawStore.Name;
    }
    if (retailerConfig.retailer === "lowes" && rawStore.Name) {
      return rawStore.Name;
    }
    if (retailerConfig.retailer === "home_depot" && rawStore.store_title) {
      return rawStore.store_title;
    }
    if (retailerConfig.retailer === "bestbuy" && rawStore.name) {
      return rawStore.name;
    }
    if ((retailerConfig.retailer === "tractor_supply" || retailerConfig.retailer === "northern_tool") && rawStore.name) {
      return rawStore.name;
    }
    if (retailerConfig.retailer === "samsclub") {
      const samsLabel = (rawStore as { label?: string }).label || rawStore.name;
      if (samsLabel) return samsLabel;
    }
    if (retailerConfig.retailer === "costco" && rawStore.warehouse_name) {
      return rawStore.warehouse_name;
    }
    if (retailerConfig.retailer === "walmart") {
      return "Walmart";
    }
    if (retailerConfig.retailer === "dollar_general") {
      return "Dollar General";
    }

    return retailerConfig.config.displayName;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-end bg-black/45 sm:items-center sm:justify-center dark:bg-black/65 sheet-backdrop-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="store-onboarding-title"
      onClick={() => !isSaving && onClose()}
    >
      <div
        className="grid max-h-[92vh] w-full grid-rows-[auto_1fr_auto] rounded-t-2xl border border-gray-200 bg-white shadow-2xl sheet-panel-in-sm sm:max-h-[85vh] sm:max-w-lg sm:rounded-2xl dark:border-dark-700 dark:bg-dark-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-2 pb-0 sm:hidden">
          <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-dark-600" />
        </div>

        {/* Header */}
        <div className="px-4 pt-3 pb-3 sm:px-5 sm:pt-5 sm:pb-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-600/10 dark:bg-primary-400/10">
                {currentStep === "categories" ? (
                  <Tag className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                ) : (
                  <MapPin className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                )}
              </div>
              <div>
                <h2 id="store-onboarding-title" className="text-base font-semibold leading-tight text-gray-900 dark:text-white">
                  {currentStep === "categories"
                    ? t("botFilters.setupCategories") || "Select Categories"
                    : t("botFilters.setupStores") || "Select Stores"}
                </h2>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-dark-300">
                  {t("botFilters.forRetailer", { retailer: retailerConfig.config.displayName }) || `for ${retailerConfig.config.displayName}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {steps.length > 1 && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-dark-700 dark:text-dark-300">
                  {currentStepIndex + 1}/{steps.length}
                </span>
              )}
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                aria-label={t("common.close") || "Close"}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 dark:text-dark-300 dark:hover:bg-dark-700 dark:hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto min-h-0 overscroll-contain">
          <div className="px-4 pb-2 sm:px-5">
            {isCategoriesLoading && currentStep === "categories" ? (
              <LoadingState />
            ) : categoriesError && currentStep === "categories" ? (
              <ErrorState message={((categoriesError as Error)?.message || "An error occurred") as string} />
            ) : (
              <>
                {currentStep === "categories" && categories && (
                  <CategoriesFilter
                    categories={categories}
                    selectedCategories={selectedCategories}
                    isSaving={isSaving}
                    onToggleCategory={toggleCategory}
                    onToggleAllCategories={toggleAllCategories}
                  />
                )}

                {currentStep === "stores" && (() => {
                  const storeLimit = user ? getStoreLimit(user.retailers || [], retailerConfig.retailer) : 5;
                  const selectedCount = selectedStores.filter(s => s).length;
                  return (
                    <div className="space-y-3">
                      <ZipCodeDisplay zipCode={zipCode} onZipCodeChange={setZipCode} />

                      {/* Search + counter */}
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400 dark:text-dark-300" />
                          <input
                            type="text"
                            placeholder={t("common.search") + "..."}
                            value={storeSearchQuery}
                            onChange={(e) => setStoreSearchQuery(e.target.value)}
                            className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-8 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500/40 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-dark-600 dark:bg-dark-700 dark:text-white dark:placeholder-dark-400 dark:focus:border-primary-500/40 dark:focus:ring-primary-500/40"
                          />
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1 text-sm text-gray-900 dark:text-white">
                          <span className="font-bold text-primary-600 dark:text-primary-400">{selectedCount}</span>
                          <span className="text-gray-400 dark:text-dark-300">/</span>
                          <span className="font-bold">{storeLimit}</span>
                          <span className="ml-0.5 text-[10px] uppercase tracking-widest text-gray-400 dark:text-dark-300">max</span>
                        </div>
                      </div>

                      {/* Store list */}
                      <div className="space-y-1 max-h-[50vh] overflow-y-auto overscroll-contain -mx-1 px-1">
                        {isGeocodingLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600 dark:border-primary-900 dark:border-t-primary-400" />
                          </div>
                        ) : filteredStores.length === 0 ? (
                          <div className="py-8 text-center text-sm text-gray-500 dark:text-dark-300">{t("addStoresModal.noStoresFound")}</div>
                        ) : (
                          filteredStores.map((store, idx) => {
                            const isSelected = selectedStores.includes(store.normalizedId);
                            const atLimit = selectedCount >= storeLimit && !isSelected;
                            const isCostco = retailerConfig.retailer === "costco";
                            const displayId = isCostco && store.normalizedId.endsWith("-wh")
                              ? store.normalizedId.replace(/-wh$/, "") : store.normalizedId;
                            const displayLabel = getStoreLabel(store);
                            return (
                              <button
                                key={store.normalizedId || idx}
                                type="button"
                                disabled={atLimit}
                                onClick={() => setSelectedStores(prev => isSelected ? prev.filter(id => id !== store.normalizedId) : [...prev.filter(s => s), store.normalizedId])}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                                  isSelected
                                    ? "bg-primary-50 ring-1 ring-primary-200 dark:bg-primary-500/15 dark:ring-primary-500/30"
                                    : atLimit
                                    ? "opacity-30 cursor-not-allowed"
                                    : "hover:bg-gray-50 active:bg-gray-100 dark:hover:bg-dark-700 dark:active:bg-dark-600"
                                }`}
                              >
                                <div className={`w-5 h-5 rounded-md flex-shrink-0 flex items-center justify-center transition-colors ${
                                  isSelected ? "bg-primary-600 dark:bg-primary-500" : "border-2 border-gray-300 dark:border-dark-500"
                                }`}>
                                  {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                </div>
                                <div className="min-w-0 flex-1 text-left">
                                  <div className="truncate text-sm font-bold text-gray-900 dark:text-white">
                                    #{displayId} - {displayLabel}
                                  </div>
                                  <div className="truncate text-sm text-gray-600 dark:text-dark-200">
                                    {store.normalizedAddress}
                                  </div>
                                </div>
                                {store.distance && (
                                  <span className="flex-shrink-0 tabular-nums text-xs text-gray-500 dark:text-dark-300">{store.distance.toFixed(0)}mi</span>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 sm:px-5 sm:pb-5 flex-shrink-0">
          {error && (
            <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800/50 dark:bg-red-900/30">
              <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
          <div className={`flex items-center ${currentStepIndex > 0 ? "gap-3" : ""}`}>
            {currentStepIndex > 0 && (
              <button
                onPointerDown={handleBack}
                disabled={isFirstStep || isSaving}
                className="flex items-center gap-1 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:text-white dark:hover:bg-dark-700"
              >
                ← {t("common.back")}
              </button>
            )}
            <div className="flex-1" />
            <AuthButton
              onClick={handleNext}
              disabled={!canProceed() || isSaving}
              className="flex items-center justify-center gap-1.5 !py-2.5 text-sm font-semibold !rounded-xl"
            >
              {isLastStep ? (
                t("botFilters.completeSetup") || "Complete Setup"
              ) : (
                <>
                  {t("common.next") || "Next"}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </AuthButton>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
